import { selectCurrentHotelId } from 'accessors'
import DragAndDropUploader from 'components/ReusableComponents/DragAndDropUploader'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import RecentMediaDialog from 'features/ManageCampaigns/RecentMediaDialog'
import Right from 'features/ManageCampaigns/RecentMediaDialog/Right'
import { getImgUrlFromCDN } from 'features/ManageCampaigns/utils/campaignHelpers'
import { maxFileSize } from 'features/Messaging/fileUploadConfig'
import useFirstRender from 'hooks/useFirstRender'
import useLocalStorage from 'hooks/useLocalStorageState'
import usePlausible from 'hooks/usePlausible'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import dashboardApi from 'services/api/dashboard'

import styles from './MediaUploader.module.scss'

const MediaUploaderMini = ({
  setSelectedMedia,
  selectedMedia,
  removeSelectedMedia,
  hasTitle = true,
  acceptedFileTypes = ['image/*'],
  previewCount = 4,
  mediaTag
}) => {
  const [addedFiles, setAddedFiles] = useState()

  useEffect(() => {
    if (!selectedMedia) {
      setAddedFiles(null)
    }
  }, [selectedMedia])

  const [isRecentMediaDialogOpen, setIsRecentMediaDialogOpen] = useState(false)

  const hotelId = useSelector(selectCurrentHotelId)

  const [recentFiles, setRecentFiles] = useLocalStorage(
    `mediaList-${mediaTag}-${hotelId}`,
    []
  )

  const [lastPositionToken, setLastPositionToken] = useLocalStorage(
    `mediaToken-${mediaTag}-${hotelId}`,
    null
  )

  const [hasAllFiles, setHasAllFiles] = useState(false)

  function getMediaList() {
    dashboardApi.getMediaList(mediaTag, lastPositionToken).then(mediaList => {
      const files = mediaList?.files || []
      const filesWithExtensions = files.filter(file => /\.(\w+)$/.test(file.fileName))

      if (filesWithExtensions?.length) {
        const normalizedMediaList = filesWithExtensions.reduce((prev, curr) => {
          return { ...prev, [curr.fileName]: curr }
        }, {})

        setRecentFiles(m => ({ ...m, ...normalizedMediaList }))

        if (lastPositionToken && lastPositionToken === mediaList?.lastPositionToken) {
          setHasAllFiles(true)
        }
      } else if (files?.length && !filesWithExtensions?.length) {
        setHasAllFiles(true)
      }
    })
  }

  useEffect(() => {
    if (recentFiles && Object.values(recentFiles).length > 0) {
      const filesWithExtensions = Object.values(recentFiles).filter(file =>
        /\.(\w+)$/.test(file.fileName)
      )
      const lastFile = filesWithExtensions[filesWithExtensions.length - 1]

      if (lastFile?.fileName?.startsWith('20')) {
        setLastPositionToken(lastFile.fileName)
      }
    }
  }, [recentFiles, setLastPositionToken])

  useFirstRender(() => {
    getMediaList()
  })

  const { postEvent, plausibleEvents } = usePlausible()

  return (
    <>
      {isRecentMediaDialogOpen && (
        <RecentMediaDialog
          handleHideDialog={() => setIsRecentMediaDialogOpen(false)}
          setSelectedMedia={setSelectedMedia}
          recentFiles={recentFiles}
          getMoreMedia={getMediaList}
          hasAllFiles={hasAllFiles}
          mediaTag={mediaTag}
        />
      )}
      {hasTitle && <p className={styles.editTextFormTitle}>Attachments</p>}
      <div
        className={addedFiles ? styles.dragAndDropHolderTall : styles.dragAndDropHolder}
      >
        {selectedMedia ? (
          <div className={styles.selectedMediaWrapper}>
            <div className={styles.selectedMediaHolder}>
              <div className={styles.selectedMedia}>
                <FilePreview
                  extension={selectedMedia.match(/\.(\w+)$/)?.[1] || ''}
                  src={getImgUrlFromCDN(selectedMedia, mediaTag, hotelId)}
                  clickable={false}
                />
              </div>
              <button
                className={styles.selectedMediaRemoveButton}
                onClick={removeSelectedMedia}
              >
                <CrossIcon fill="white" />
              </button>
            </div>
            <p className={styles.selectedFileName}>
              {selectedMedia.replace(/\d{14}__swaypay__/, '').replace(/(c|a)\//, '')}
            </p>
          </div>
        ) : (
          <DragAndDropUploader
            url="/media"
            acceptedFileTypes={acceptedFileTypes}
            maxFileSize={maxFileSize}
            maxFiles={1}
            onLoad={res => {
              const data = JSON.parse(res)
              postEvent(plausibleEvents.UpdateCampaignMedia)
              setSelectedMedia(data?.result?.temporaryUrl?.fileName)
            }}
            onUpdateFiles={files => setAddedFiles(files)}
            onData={formData => {
              formData.append('tag', mediaTag)
              return formData
            }}
            labelIdle="<span class='filepond--label-action-mini-left-aligned'>Select file</span>"
          />
        )}
      </div>
      {Object.values(recentFiles).length > 0 && !selectedMedia && !addedFiles && (
        <div>
          <p className={styles.recentFilesTitle}>Select from recent files</p>
          <div className={styles.thumbnailHolder}>
            {Object.values(recentFiles)
              .filter(file => /\.(\w+)$/.test(file.fileName))
              .reverse()
              .slice(0, previewCount)
              .map(file => (
                <>
                  <div
                    className={styles.thumbnail}
                    style={{
                      border:
                        file.fileName === selectedMedia
                          ? '2px solid var(--linkBlue)'
                          : '2px solid transparent'
                    }}
                    onClick={() => setSelectedMedia(file.fileName)}
                  >
                    <div className={styles.fileHolder}>
                      <FilePreview
                        extension={file.fileName.match(/\.(\w+)$/)?.[1] || ''}
                        src={getImgUrlFromCDN(file.fileName, mediaTag, hotelId)}
                        clickable={false}
                        className={styles.thumbnailInner}
                      />
                      <span className={styles.fileName}>
                        {file.fileName.replace(/\d{14}__swaypay__/, '')}
                      </span>
                    </div>
                  </div>
                </>
              ))}
            {Object.values(recentFiles).filter(file => /\.(\w+)$/.test(file.fileName))
              .length > previewCount && (
              <button
                className={styles.seeMoreButton}
                onClick={() => setIsRecentMediaDialogOpen(true)}
              >
                <Right width={20} height={30} />
                <span>See all</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

MediaUploaderMini.propTypes = {
  handleUploadedFile: PropTypes.func,
  mediaTag: PropTypes.oneOf(['c', 'a']) // [C]ampaign or [A]nnouncement
}

export default MediaUploaderMini
